/**
 * The primitives every page is built from.
 *
 * ---------------------------------------------------------------------------
 * THREE CHANNELS, ENFORCED IN ONE PLACE
 * ---------------------------------------------------------------------------
 * `StatusPill` and `SignalValue` are the only two components allowed to render
 * a backend status, and each of them always emits all three channels:
 *
 *   the WORD       `lib/signal.ts#labelFor`  — never empty, never a bare symbol
 *   the ICON       `lib/signal.ts#iconFor`   — a tick, a minus, a dashed ring,
 *                                              a strike, a cross
 *   the CONTAINER  `styles.css .epi-*`       — filled / dotted / dashed
 *
 * None of the three is hue. Hue is added on top, by `isAlarm`, and only for
 * the handful of states that ask for a decision — so a page full of honest
 * unknowns renders in ink and the one thing that is actually failing is the
 * only coloured mark on the screen.
 *
 * No page formats a status itself. That is what keeps "never a green tick for
 * an unmeasured thing" a property of the build rather than a habit, and it now
 * also keeps the stronger claim: never a FILLED container for an unmeasured
 * thing, because filled-versus-outlined is legible before the word is.
 */

import type { ReactNode } from 'react';

import {
  type Epistemic,
  type PercentileSignalView,
  type Signal,
  type Tone,
  type ValueTextOptions,
  describeStatus,
  epistemicFor,
  explain,
  iconFor,
  isAlarm,
  isMeasurementStatus,
  labelFor,
  percentileText,
  toneFor,
  valueText,
} from '../lib/signal';
import { Icon, type IconName } from './icons';

/* ------------------------------------------------------------------ *
 * The channels as class names
 * ------------------------------------------------------------------ */

/** Hue, for the few states that are allowed one. */
const TONE_TEXT: Record<Tone, string> = {
  good: 'text-good',
  bad: 'text-bad',
  warn: 'text-warn',
  blocked: 'text-ink-3',
  neutral: 'text-ink-3',
};

/** AXIS 1 — the container. */
const EPI_CLASS: Record<Epistemic, string> = {
  measured: 'epi-measured',
  unmeasured: 'epi-unmeasured',
  unmeasurable: 'epi-unmeasurable',
};

/**
 * The container class for a status, or a plain fill when the status is not a
 * measurement.
 *
 * `GREEN`, `AMBER`, `RED`, `healthy`, `degraded` and the ledger's workflow
 * states are conclusions, not readings. Outlining them would spend the
 * epistemic channel on things it does not describe, and a vocabulary that
 * means everything means nothing — so they get the filled container and the
 * dotted/dashed edge stays reserved for "did we look?".
 */
export function epiClass(status: string): string {
  if (!isMeasurementStatus(status)) return 'epi-measured';
  return EPI_CLASS[epistemicFor(status)];
}

export function toneTextClass(tone: Tone): string {
  return TONE_TEXT[tone];
}

/**
 * The ink or hue a status is drawn in.
 *
 * Everything is ink unless it is an alarm. See `lib/signal.ts#isAlarm` for why
 * that is the right trade and why it does not weaken the vocabulary.
 */
export function statusTextClass(status: string): string {
  return isAlarm(status) ? TONE_TEXT[toneFor(status)] : 'text-ink-2';
}

/* ------------------------------------------------------------------ *
 * Status chip
 * ------------------------------------------------------------------ */

export function StatusPill({
  status,
  title,
  size = 'md',
  icon,
  children,
}: {
  status: string;
  title?: string | null;
  size?: 'sm' | 'md';
  /** Override the vocabulary icon. Used only by the reading key. */
  icon?: IconName;
  children?: ReactNode;
}): ReactNode {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border font-medium ${
        size === 'sm' ? 'px-1.5 py-0.5 text-2xs' : 'px-2 py-1 text-xs'
      } ${statusTextClass(status)} ${epiClass(status)}`}
      // The tooltip explains the EPISTEMIC meaning, which is the part an
      // operator has to internalise; the backend's own reason, when there is
      // one, is carried by the caller next to the chip.
      title={title ?? describeStatus(status)}
    >
      {/* Decorative: the word beside it carries the meaning. */}
      <Icon name={icon ?? iconFor(status)} size={size === 'sm' ? 12 : 13} />
      <span>{children ?? labelFor(status)}</span>
    </span>
  );
}

/**
 * The chip for "we do not have this", where there is no backend status object
 * to pass — a missing node in a placement map, a request series that returned
 * no row for one resource.
 *
 * It exists so those cases cannot drift: they are `unmeasured`, they get the
 * dotted container and the dashed-ring icon, and they are drawn in ink like
 * every other honest absence.
 */
export function Absent({
  children,
  title,
  size = 'md',
}: {
  children: ReactNode;
  title?: string;
  size?: 'sm' | 'md';
}): ReactNode {
  return (
    <span
      className={`epi-unmeasured inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border text-ink-3 ${
        size === 'sm' ? 'px-1.5 py-0.5 text-2xs' : 'px-2 py-0.5 text-xs'
      }`}
      title={title}
    >
      <Icon name="unproven" size={size === 'sm' ? 12 : 13} />
      <span>{children}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Measured quantities
 * ------------------------------------------------------------------ */

/**
 * A measured quantity.
 *
 * `ok` → the number, in tabular figures, in normal ink: a reading is not an
 * event and should not shout, and it certainly should not wear a green tick.
 * Anything else → the WORD, its icon, and its container. There is no code path
 * that emits a bare `0` or an empty cell for a signal that is not `ok`.
 */
export function SignalValue({
  signal,
  format,
  unit,
  className,
}: {
  signal: Signal<unknown> | undefined;
  format?: ValueTextOptions['format'];
  unit?: string;
  className?: string;
}): ReactNode {
  if (signal === undefined) {
    return <Absent title="the API did not return this field at all">missing</Absent>;
  }
  const options: ValueTextOptions = {};
  if (format !== undefined) options.format = format;
  if (unit !== undefined) options.unit = unit;
  const text = valueText(signal, options);
  const why = explain(signal);

  if (signal.status === 'ok') {
    return <span className={`num ${className ?? ''}`}>{text}</span>;
  }

  return (
    <span
      // `whitespace-nowrap`, and `items-start` for the case the container is
      // still too narrow: a chip whose word wraps under its own icon is
      // unreadable in a table cell, and "below request floor" in a 110 px
      // column did exactly that.
      className={`inline-flex items-start gap-1.5 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-xs ${statusTextClass(signal.status)} ${epiClass(signal.status)}`}
      title={why === null ? describeStatus(signal.status) : `${describeStatus(signal.status)} — ${why}`}
    >
      <Icon name={iconFor(signal.status)} size={13} className="mt-px" />
      <span>{text}</span>
      {why !== null && <span className="sr-only">: {why}</span>}
    </span>
  );
}

/** A latency percentile. Its two special statuses are printed as sentences. */
export function PercentileValue({
  p,
  emphasis = false,
  compact = false,
}: {
  p: PercentileSignalView | undefined;
  emphasis?: boolean;
  /**
   * Print the bare WORD for the two edge statuses instead of the full sentence.
   *
   * `percentileText` renders `below_request_floor` as "below request floor
   * (12 of 100)", which is the right answer in a table cell and four wrapped
   * lines in a 110 px column on a phone. Compact prints "below request floor"
   * — still the word, still never a number it does not have — and keeps the
   * counts in the tooltip and in the screen-reader text, so nothing is lost.
   */
  compact?: boolean;
}): ReactNode {
  if (p === undefined) return <Absent size="sm">missing</Absent>;
  const why = explain(p as Signal<unknown>);
  const full = percentileText(p);
  const shown = compact ? labelFor(p.status) : full;

  if (p.status === 'ok') {
    return (
      <span
        className={`num ${emphasis ? 'text-md font-semibold' : ''}`}
        title={`p${String(Math.round((p.quantile ?? 0) * 100))} over ${String(p.requests ?? 0)} requests`}
      >
        {full}
      </span>
    );
  }
  return (
    <span
      // The compact word never wraps — a chip whose two words stack under its
      // own icon is unreadable in a 110 px column. The FULL sentence is allowed
      // to wrap, because "below request floor (12 of 100)" set on one line is
      // 210 px and would spill out of the cell it belongs to.
      className={`inline-flex items-start gap-1.5 rounded-md border px-1.5 py-0.5 text-xs ${
        compact ? 'whitespace-nowrap' : ''
      } ${statusTextClass(p.status)} ${epiClass(p.status)}`}
      title={`${full}. ${describeStatus(p.status)}${why === null ? '' : ` — ${why}`}`}
    >
      <Icon name={iconFor(p.status)} size={13} className="mt-px" />
      <span>{shown}</span>
      {/* The full sentence, with its counts, always reaches assistive tech. */}
      {compact && <span className="sr-only">{full}</span>}
      {why !== null && <span className="sr-only">: {why}</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Containers
 * ------------------------------------------------------------------ */

/**
 * A card.
 *
 * Generous padding (20 px, 24 px on desktop) and a real gap between the title
 * and the body. Density is still a requirement — an operator wants a lot on
 * screen — but density comes from tight LINE spacing inside a block, not from
 * blocks that touch each other.
 *
 * The `subtitle` is for a NOUN PHRASE, not a paragraph. Explanations belong in
 * the reading key or a tooltip; a card that argues with you under its own title
 * is a card whose data you stop reading.
 */
export function Card({
  title,
  subtitle,
  icon,
  right,
  footer,
  children,
  id,
  padding = 'normal',
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: IconName;
  right?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  id?: string;
  padding?: 'normal' | 'flush';
}): ReactNode {
  return (
    <section id={id} className="overflow-hidden rounded-card border border-line bg-surface">
      {(title !== undefined || right !== undefined) && (
        /*
         * Wraps below `sm`. A card title plus a four-button range picker does
         * not fit on one line at 390 px, and the previous single-row header
         * resolved that by truncating the title — which produced "CNPG
         * replication la" and "Allocatable per nod" on the phone. The title is
         * the label; it does not get cut so a control can stay on its line.
         */
        <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 pb-4 pt-5">
          <div className="flex min-w-0 flex-1 basis-48 items-start gap-2.5">
            {icon !== undefined && <Icon name={icon} size={17} className="mt-0.5 text-ink-4" />}
            <div className="min-w-0">
              {title !== undefined && (
                <h2 className="text-md font-semibold leading-snug tracking-tight text-ink">
                  {title}
                </h2>
              )}
              {subtitle !== undefined && (
                /*
                 * Clamped: some catalogue descriptions are whole paragraphs. A
                 * clamp still ends mid-sentence, so when the subtitle is plain
                 * text it also carries the whole string in `title` — the cut is
                 * then recoverable rather than final.
                 */
                <p
                  className="mt-1 line-clamp-3 text-xs leading-snug text-ink-3"
                  {...(typeof subtitle === 'string' ? { title: subtitle } : {})}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {right !== undefined && <div className="shrink-0">{right}</div>}
        </header>
      )}
      <div className={padding === 'flush' ? '' : 'px-5 pb-5'}>{children}</div>
      {footer !== undefined && (
        <div className="border-t border-line bg-surface-2/50 px-5 py-3 text-xs text-ink-3">
          {footer}
        </div>
      )}
    </section>
  );
}

export function KeyValue({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-xs text-ink-3" title={hint}>
        {label}
      </dt>
      <dd className="min-w-0 text-right text-sm text-ink">{children}</dd>
    </div>
  );
}

/**
 * A headline number.
 *
 * Uppercase micro-type label, 30 px numeral with negative tracking and tabular
 * figures. `tone` is applied to the NUMBER only, and only when the tone is an
 * ALARM — a tile washed in green for "0 critical" is a tile you stop noticing,
 * and it spends the alarm colour on the steady state.
 */
export function Metric({
  label,
  value,
  sub,
  tone = 'neutral',
  icon,
  onClick,
  emphasis = false,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  icon?: IconName;
  onClick?: () => void;
  emphasis?: boolean;
}): ReactNode {
  const alarm = tone === 'bad' || tone === 'warn';
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-2xs font-medium uppercase tracking-[0.1em] text-ink-3">{label}</p>
        {icon !== undefined && <Icon name={icon} size={15} className="text-ink-4" />}
      </div>
      <p
        className={`num mt-3 font-semibold ${emphasis ? 'text-2xl' : 'text-xl'} ${
          alarm ? TONE_TEXT[tone] : 'text-ink'
        }`}
      >
        {value}
      </p>
      {/*
        Three tiers, and they must not sit at the same weight: the LABEL is
        uppercase micro-type, the VALUE is 30 px, and the SUB is sentence-case
        micro-type. Label and sub share a size and a colour, so case and tracking
        are what separate them — which is enough, and is cheaper than spending
        another ink step that would fall under the contrast floor.
      */}
      {sub !== undefined && (
        <p className="mt-2 text-2xs leading-snug text-ink-3">{sub}</p>
      )}
    </>
  );
  /*
   * `p-5` on the phone too, not `p-4`. The metric tiles were the one card in the
   * system with tighter padding than every other card, which is what made them
   * read as cramped against their own borders next to the hero.
   */
  const shell = 'rounded-card border border-line bg-surface p-5 text-left sm:p-6';
  if (onClick === undefined) return <div className={shell}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${shell} block w-full transition-colors hover:border-line-strong hover:bg-surface-2`}
    >
      {body}
    </button>
  );
}

/**
 * The empty/error state for a whole panel.
 *
 * Says which of the two it is, and wears the container of the status it is
 * reporting — so a panel that is empty because nothing matched does not look
 * like a panel that is empty because nobody looked. A spinner that never
 * resolves and a genuinely empty ledger must not look the same either.
 */
export function Placeholder({
  tone = 'neutral',
  status,
  title,
  children,
}: {
  tone?: Tone;
  /** When given, the container and icon come from the real status. */
  status?: string;
  title: string;
  children?: ReactNode;
}): ReactNode {
  const effectiveStatus = status ?? (tone === 'bad' ? 'error' : tone === 'warn' ? 'unproven' : 'empty');
  const alarm = status === undefined ? tone === 'bad' || tone === 'warn' : isAlarm(status);
  return (
    <div
      className={`rounded-xl border px-4 py-6 text-center ${
        alarm ? TONE_TEXT[status === undefined ? tone : toneFor(status)] : 'text-ink-2'
      } ${status === undefined ? 'epi-measured' : epiClass(status)}`}
      role={tone === 'bad' || status === 'error' ? 'alert' : undefined}
    >
      <p className="flex items-center justify-center gap-2 text-sm font-medium">
        <Icon name={iconFor(effectiveStatus)} size={16} />
        {title}
      </p>
      {children !== undefined && (
        <p className="mx-auto mt-1.5 max-w-prose text-xs leading-relaxed text-ink-3">{children}</p>
      )}
    </div>
  );
}

/**
 * The loading state, as a real announcement.
 *
 * Every page in this application was pulsing an anonymous `<div>` while it
 * waited. That is fine for a sighted reader and silent for everyone else, and
 * silence is the one thing this dashboard is not allowed to be: a screen reader
 * met an empty document and had no way to tell "still fetching" from "there is
 * nothing here", which is the same `unproven`-versus-`empty` confusion the whole
 * vocabulary exists to prevent — just on the time axis instead of the data one.
 *
 * So the skeleton says what it is waiting for, out loud, and looks like the
 * thing that is coming so the layout does not jump when it lands.
 */
export function Skeleton({
  label,
  shapes = ['h-28', 'h-64'],
  className,
}: {
  /** What is loading, as a noun phrase: "the overview", "this run". */
  label: string;
  /** Height utilities for the blocks to draw, top to bottom. */
  shapes?: string[];
  className?: string;
}): ReactNode {
  return (
    <div
      className={`space-y-3 ${className ?? ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {shapes.map((shape, index) => (
        <div
          key={index}
          aria-hidden="true"
          className={`${shape} animate-pulse rounded-card border border-line bg-surface`}
        />
      ))}
      <p className="sr-only">loading {label}…</p>
    </div>
  );
}

/**
 * A signal that wraps a whole panel: renders `children(value)` for `ok`, and the
 * honest explanation otherwise.
 */
export function SignalPanel<T>({
  signal,
  emptyTitle,
  children,
}: {
  signal: Signal<T> | undefined;
  emptyTitle?: string;
  children: (value: T) => ReactNode;
}): ReactNode {
  if (signal === undefined) {
    return <Placeholder tone="warn" title="the API did not return this section" />;
  }
  if (signal.status === 'ok' && signal.value !== undefined) {
    return <>{children(signal.value)}</>;
  }
  return (
    <Placeholder status={signal.status} title={emptyTitle ?? labelFor(signal.status)}>
      {explain(signal)}
    </Placeholder>
  );
}

/* ------------------------------------------------------------------ *
 * Small shared bits
 * ------------------------------------------------------------------ */

/** A micro-label. Uppercase, wide-tracked, dim — the section marker. */
export function Eyebrow({ children }: { children: ReactNode }): ReactNode {
  return <p className="text-2xs font-medium uppercase tracking-[0.1em] text-ink-3">{children}</p>;
}

/**
 * A proportion bar.
 *
 * It NEVER carries the value alone: `Bar` requires the caller to have printed
 * the numbers next to it, and it puts them in its own `aria-label` too. A bar
 * for an unmeasured quantity is not offered by this component at all — the
 * caller renders `SignalValue` instead, which is why there is no `signal` prop
 * here.
 */
export function Bar({
  fraction,
  tone,
  label,
  height = 'h-1',
}: {
  fraction: number;
  tone: Tone;
  label: string;
  height?: string;
}): ReactNode {
  const TONE_BG: Record<Tone, string> = {
    good: 'bg-ink-2',
    bad: 'bg-bad',
    warn: 'bg-warn',
    blocked: 'bg-ink-3',
    neutral: 'bg-ink-3',
  };
  return (
    <div
      className={`${height} overflow-hidden rounded-full bg-white/7`}
      role="img"
      aria-label={label}
    >
      <div
        className={`h-full rounded-full ${TONE_BG[tone]}`}
        style={{ width: `${String(Math.min(100, Math.max(0, fraction * 100)))}%` }}
      />
    </div>
  );
}

/** A dense identifier — pod names, ingress hosts, finding keys, run ids. */
export function Ident({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return <span className={`mono text-xs text-ink-2 ${className ?? ''}`}>{children}</span>;
}

export { Icon };
